import { PageHero, Reveal, SectionEyebrow } from "@/components/Primitives";
import { INDICES, FRAMEWORKS, IMAGES } from "@/lib/constants";
import { motion } from "framer-motion";
import { ChevronRight } from "lucide-react";

export default function Science() {
  return (
    <div data-testid="science-page">
      <PageHero
        testId="science-hero"
        eyebrow="The Science"
        title="Measuring what architecture forgot."
        lead="The Institute studies the built environment as a maintainable system. Surfaces, materials, geometries, labour, time, chemistry, dignity. The discipline rests on a small set of indices and frameworks. Below, the working tools of the field."
      />

      {/* indices */}
      <section className="container-x section-y border-t border-border">
        <SectionEyebrow>Composite indices</SectionEyebrow>
        <h2 className="mt-6 font-display text-3xl md:text-5xl tracking-tight font-medium max-w-3xl text-balance">
          Four numbers that change every conversation.
        </h2>
        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-4">
          {INDICES.map((idx, i) => (
            <Reveal key={idx.code} delay={i * 0.06}>
              <div data-testid={`science-index-${idx.code}`} className="rounded-2xl border border-border bg-card p-8 md:p-10 hover-lift">
                <div className="flex items-baseline justify-between">
                  <div>
                    <div className="font-mono-mini text-muted-foreground">{idx.code}</div>
                    <div className="mt-2 font-display text-2xl font-medium tracking-tight">{idx.name}</div>
                  </div>
                  <div className="font-display text-5xl font-medium text-[hsl(var(--accent))] tracking-tight">{idx.value}</div>
                </div>
                <p className="mt-6 text-base text-muted-foreground leading-relaxed">{idx.description}</p>
                <div className="mt-8 h-1 rounded-full bg-border overflow-hidden">
                  <motion.div
                    initial={{ width: 0 }}
                    whileInView={{ width: `${idx.value}%` }}
                    viewport={{ once: true }}
                    transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
                    className="h-full bg-[hsl(var(--accent))]"
                  />
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* frameworks */}
      <section className="border-y border-border bg-secondary/30">
        <div className="container-x section-y">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-4">
              <SectionEyebrow>Working frameworks</SectionEyebrow>
              <h2 className="mt-6 font-display text-3xl md:text-5xl tracking-tight font-medium text-balance">
                Concepts the Institute uses daily.
              </h2>
              <p className="mt-6 text-muted-foreground">
                A field is not a field until it shares a vocabulary. These are ours.
              </p>
            </div>
            <div className="lg:col-span-8 grid grid-cols-1 md:grid-cols-2 gap-px bg-border rounded-2xl overflow-hidden border border-border">
              {FRAMEWORKS.map((f, i) => (
                <div key={f.title} data-testid={`framework-${i}`} className="bg-card p-8 hover-lift group">
                  <div className="flex items-start justify-between gap-4">
                    <div className="font-mono-mini text-muted-foreground">{String(i + 1).padStart(2, "0")}</div>
                    <ChevronRight className="h-4 w-4 text-muted-foreground group-hover:text-[hsl(var(--accent))] transition-colors" />
                  </div>
                  <div className="mt-3 font-display text-lg md:text-xl font-medium tracking-tight">{f.title}</div>
                  <p className="mt-3 text-sm text-muted-foreground leading-relaxed">{f.body}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* the gap */}
      <section className="container-x section-y">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6">
            <SectionEyebrow>The design-performance gap</SectionEyebrow>
            <h2 className="mt-6 font-display text-3xl md:text-5xl tracking-tight font-medium text-balance">
              The measurable distance between handover and reality.
            </h2>
            <p className="mt-6 text-base md:text-lg text-muted-foreground leading-relaxed">
              On day one a building is photographed and celebrated. By year five it has settled into
              an operational reality of friction, compromise, and quiet erosion. The gap between those
              two states is measurable — and largely designable.
            </p>
            <div className="mt-10 grid grid-cols-3 gap-3">
              {[
                ["Year 0", 92, "Handover"],
                ["Year 5", 64, "Settled"],
                ["Year 15", 41, "Drift"],
              ].map(([y, v, l], i) => (
                <div key={i} data-testid={`gap-${i}`} className="rounded-xl border border-border bg-card p-5">
                  <div className="font-mono-mini text-muted-foreground">{y}</div>
                  <div className="font-display text-3xl md:text-4xl font-medium tracking-tight">{v}</div>
                  <div className="text-xs text-muted-foreground mt-1">{l}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="lg:col-span-6">
            <div className="relative rounded-2xl overflow-hidden border border-border">
              <img src={IMAGES.ciGraphic} alt="CI graphic" className="w-full h-auto" />
            </div>
          </div>
        </div>
      </section>

      {/* maturity */}
      <section className="border-t border-border bg-secondary/40">
        <div className="container-x section-y">
          <SectionEyebrow>Cleanability Maturity Model</SectionEyebrow>
          <h2 className="mt-6 font-display text-3xl md:text-5xl tracking-tight font-medium max-w-3xl text-balance">Five stages from reactive to systemic.</h2>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-5 gap-3">
            {[
              { stage: "01", name: "Reactive", body: "Cleanliness as residue of operations. No standards. No data." },
              { stage: "02", name: "Compliance", body: "Standards adopted but enforced unevenly. Limited measurement." },
              { stage: "03", name: "Operational", body: "Measurable outcomes. Trained teams. Functional dashboards." },
              { stage: "04", name: "Integrated", body: "Cleanability briefed into design, procurement, and lifecycle." },
              { stage: "05", name: "Systemic", body: "An organisational discipline. Continuously researched and improved." },
            ].map((s, i) => (
              <Reveal key={s.stage} delay={i * 0.05}>
                <div data-testid={`maturity-${i}`} className="rounded-2xl border border-border bg-card p-6 h-full">
                  <div className="font-mono-mini text-[hsl(var(--accent))]">Stage {s.stage}</div>
                  <div className="mt-3 font-display text-lg font-medium tracking-tight">{s.name}</div>
                  <p className="mt-3 text-sm text-muted-foreground leading-relaxed">{s.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
